import { useEffect, useState } from 'react';

export const ROMAN_FOLDS = [['flat', 'Flat'], ['hobbled', 'Hobbled'], ['relaxed', 'Relaxed'], ['cascade', 'Cascade'], ['aventura', 'Aventura'], ['pleated', 'Pleated']];
export const FOLD_INFO = {
  flat: 'Smooth flat face, minimal seams; stacks neatly at the bottom',
  hobbled: 'Soft, full cascading loops down the whole height',
  relaxed: 'Clean face with a broad, shallow smile at the bottom',
  cascade: 'Crisp, evenly spaced fold lines across the face',
  aventura: 'Straight, evenly spaced front battens — modern',
  pleated: 'Crisp, sharp pleats from top to bottom',
};
export const LINING_INFO = {
  unlined: 'Standard — natural glow',
  privacy: 'Soft glow, more privacy',
  blackout: 'Blocks light almost completely',
  interlining: 'Fuller, softer, insulating',
};
export const foldLabel = (f) => (ROMAN_FOLDS.find(([id]) => id === f) || ROMAN_FOLDS[0])[1];

/* Simulator fold id ↔ pricing fold_style id (Admin → Pricing) */
export const FOLD_TO_PRICE_ID = { flat: 'flat-fold', hobbled: 'hobbled', relaxed: 'soft-fold', cascade: 'cascade', aventura: 'aventura', pleated: 'pleated' };
export const priceIdToFold = (id) => Object.keys(FOLD_TO_PRICE_ID).find(k => FOLD_TO_PRICE_ID[k] === id) || 'flat';

export const ROMAN_LININGS = [['unlined', 'Unlined'], ['privacy', 'Privacy'], ['blackout', 'Blackout'], ['interlining', 'Interlining']];
export const liningLabel = (l) => (ROMAN_LININGS.find(([id]) => id === l) || ROMAN_LININGS[0])[1];

/* Lining darkens the Simulator glow: privacy lets little light through, blackout none */
export const linedLight = (light, filterOpacity, lining) => {
  if (lining === 'blackout') return { light: { ...light, mode: 'blackout', label: 'Blackout lining' }, filterOpacity: 1 };
  if (lining === 'interlining') return { light: { ...light, mode: light.mode === 'blackout' ? 'blackout' : 'filtering', label: 'Interlined', body: true }, filterOpacity: Math.max(filterOpacity, 0.93) };
  if (lining === 'privacy') return { light: { ...light, mode: 'filtering', label: 'Privacy lining' }, filterOpacity: Math.max(filterOpacity, 0.97) };
  return { light, filterOpacity };
};

/* Shared choice persisted in localStorage and broadcast so every component stays in sync */
const sharedChoice = (key, fallback) => {
  const event = `${key}-change`;
  const read = () => { try { return localStorage.getItem(key) || fallback; } catch { return fallback; } };
  const set = (v) => {
    if (v === read()) return;
    try { localStorage.setItem(key, v); } catch { /* storage unavailable */ }
    window.dispatchEvent(new CustomEvent(event, { detail: v }));
  };
  const subscribe = (cb) => {
    const h = (e) => cb(e.detail);
    window.addEventListener(event, h);
    return () => window.removeEventListener(event, h);
  };
  const useChoice = () => {
    const [v, setV] = useState(read);
    useEffect(() => subscribe(setV), []);
    return [v, set];
  };
  return { set, subscribe, useChoice };
};

const fold = sharedChoice('apollo_roman_fold', 'flat');
const lining = sharedChoice('apollo_roman_lining', 'unlined');

export const setRomanFold = fold.set;
export const onRomanFoldChange = fold.subscribe;
export const useRomanFold = fold.useChoice;
export const setRomanLining = lining.set;
export const onRomanLiningChange = lining.subscribe;
export const useRomanLining = lining.useChoice;

/* Transient lining hover (not persisted) so the scene can preview a lining before it is chosen */
export const setLiningHover = (v) => window.dispatchEvent(new CustomEvent('apollo-lining-hover', { detail: v || null }));
export const useLiningHover = () => {
  const [v, setV] = useState(null);
  useEffect(() => {
    const h = (e) => setV(e.detail);
    window.addEventListener('apollo-lining-hover', h);
    return () => window.removeEventListener('apollo-lining-hover', h);
  }, []);
  return v;
};
